import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import { useSelector } from "react-redux";
import moment from "moment";
import AdminRembursmentList from "./AdminRembursmentList";
import { rembursment, status } from "../../utils/Helpers";
import { getReimbursementRequest } from "../../services";
import { toast } from "react-toastify";
import Pagination from "../../components/Pagination";
import _ from "lodash";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  Search,
  Users,
  CalendarDays,
  Tag,
  CircleDot,
  Loader2,
  ChevronDown,
} from "lucide-react";
import CustomSelect from "../../components/CustomSelect";
import DateBox from "../../components/DateBox";

const AdminRembursment = () => {
  const [dateRange, setDateRange] = useState([
    moment().startOf("month").toDate(),
    moment().toDate(),
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");
  const [selectedRequestType, setSelectedRequestType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [allRequests, setAllRequests] = useState([]);

  const itemsPerPage = 10;
  const members = useSelector((state) => state.members.members);

  const notify = (message) => {
    toast.error(message, {
      position: "top-right",
      className: "dark:bg-gray-900",
    });
  };

  const fetchReimbursementRequest = async (off) => {
    if (!dateRange[0] || !dateRange[1]) {
      notify("Please select a valid date range!");
      return;
    }
    setIsLoading(true);
    try {
      const formattedStartDate = moment(dateRange[0]).format("YYYY-MM-DD");
      const formattedEndDate = moment(dateRange[1]).format("YYYY-MM-DD");
      const response = await getReimbursementRequest(
        selectedMember,
        formattedStartDate,
        formattedEndDate,
        selectedStatus,
        selectedRequestType || "0",
        off,
        itemsPerPage
      );
      const requests = response?.data?.Result;
      setAllRequests(requests);
    } catch (err) {
      console.error("Error fetching requests:", err);
      notify("Error fetching requests!");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReimbursementRequest(offset);
  }, [offset]);

  const handleSearch = () => {
    setOffset(0);
    fetchReimbursementRequest(0);
  };

  const memberOptions = {
    "": "All Members",
    ...Object.fromEntries((members || []).map((m) => [String(m.Id), m.MemberName])),
  };

  const statusOptions = {
    "": "All Statuses",
    ...Object.fromEntries(Object.entries(status).map(([k, v]) => [k, v])),
  };

  const requestTypeOptions = {
    "": "All Types",
    ...Object.fromEntries(Object.entries(rembursment).map(([k, v]) => [k, v])),
  };

  const startDate = dateRange[0];
  const endDate = dateRange[1];

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="px-4 py-4 max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-blue-500/10 rounded-xl">
            <ClipboardList className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              All Reimbursement Requests
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Filter and manage reimbursement requests by member, date and status
            </p>
          </div>
        </div>

        {/* Filter Card */}
        <div className="bg-white dark:bg-gray-900/50 backdrop-blur-xl border border-gray-100 dark:border-blue-gray-900 p-6 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-none mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[0.8fr_0.8fr_1fr_0.8fr] gap-5 items-end">
            {/* Member */}
            <div className="space-y-2">
              <label className="text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                Member
              </label>
              <CustomSelect
                options={memberOptions}
                value={selectedMember}
                onChange={(e) => setSelectedMember(e.target.value)}
                placeholder="All Members"
                disabled={isLoading}
              />
            </div>

            {/* Request Type */}
            <div className="space-y-2">
              <label className="text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                <Tag className="w-4 h-4 text-gray-400" />
                Request Type
              </label>
              <CustomSelect
                options={requestTypeOptions}
                value={selectedRequestType}
                onChange={(e) => setSelectedRequestType(e.target.value)}
                placeholder="All Types"
                disabled={isLoading}
              />
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <label className="text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-400" />
                Date Range
              </label>
              <div className="relative group">
                <button
                  type="button"
                  onClick={() => setShowDatePicker((p) => !p)}
                  disabled={isLoading}
                  className="w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 group shadow-sm dark:shadow-none disabled:opacity-60"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <div className="bg-white dark:bg-white/10 px-3 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent text-[13px] truncate whitespace-nowrap overflow-hidden max-w-[100px] sm:max-w-none">
                      {startDate ? moment(startDate).format("DD MMM YYYY") : "Start Date"}
                    </div>
                    <span className="text-gray-400 dark:text-gray-500 font-medium text-[12px] px-0.5">To</span>
                    <div className="bg-white dark:bg-white/10 px-3 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent text-[13px] truncate whitespace-nowrap overflow-hidden max-w-[100px] sm:max-w-none">
                      {endDate ? moment(endDate).format("DD MMM YYYY") : "End Date"}
                    </div>
                  </div>
                  <div className="px-2 border-l border-gray-100 dark:border-white/10 ml-1">
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showDatePicker ? "rotate-180 text-blue-500" : ""
                        }`}
                    />
                  </div>
                </button>

                <AnimatePresence>
                  {showDatePicker && (
                    <DateBox
                      mode="range"
                      initialStartDate={dateRange[0]}
                      initialEndDate={dateRange[1]}
                      onApply={(range) => {
                        if (Array.isArray(range)) {
                          setDateRange(range);
                          if (range[0] && range[1]) setShowDatePicker(false);
                        }
                      }}
                      onClose={() => setShowDatePicker(false)}
                      maxDate={new Date()}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                <CircleDot className="w-4 h-4 text-gray-400" />
                Status
              </label>
              <CustomSelect
                options={statusOptions}
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                placeholder="All Statuses"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Search Button */}
          <div className="flex justify-end mt-5">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={handleSearch}
              disabled={isLoading}
              className="flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 transition-all disabled:opacity-70 disabled:cursor-not-allowed text-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Search</span>
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Results */}
        <AdminRembursmentList
          allRequests={allRequests}
          setAllRequests={setAllRequests}
        />

        {!_.isEmpty(allRequests) && allRequests?.TotalCount > 0 && (
          <div className="mt-6">
            <Pagination
              totalCount={allRequests?.TotalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={(newOffset) => {
                setOffset(newOffset);
                fetchReimbursementRequest(newOffset);
              }}
            />
          </div>
        )}
      </motion.div>
    </Layout>
  );
};

export default AdminRembursment;
